import { useState, useCallback } from 'react';
import { message } from 'antd';
import { d206Api, FD160, FD161 } from '../../../services/d206Api';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useD206Mode';

export function useD206Record() {
  const [record, setRecord] = useState<FD160 | null>(null);
  const [formData, setFormData] = useState<Partial<FD160>>({});
  const [details, setDetails] = useState<Partial<FD161>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const { setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<FD160 | null> => {
    setLoading(true);
    try {
      const data = await d206Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      setSelectedRow(0);
      return data;
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FD160, value: FD160[keyof FD160]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FD160): FD160[keyof FD160] => {
    return (formData as Record<string, FD160[keyof FD160]>)[key] ?? '';
  }, [formData]);

  /** Get a detail field value from the selected row */
  const getDetailField = useCallback((key: keyof FD161): FD161[keyof FD161] => {
    const sel = details[selectedRow] as Partial<FD161> | undefined;
    return sel ? (sel as Record<string, FD161[keyof FD161]>)[key] ?? '' : '';
  }, [details, selectedRow]);

  /** Set a detail field value on the selected row */
  const setDetailField = useCallback((key: keyof FD161, value: FD161[keyof FD161]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      return next;
    });
  }, [selectedRow]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FD160) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FD160,
        details: details as FD161[],
      };
      if (mode === 'add') {
        const result = await d206Api.create(req);
        message.success(`新增成功: ${result.fd160_01}`);
        onSuccess(result);
      } else {
        const result = await d206Api.update(formData.fd160_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await d206Api.delete(record.fd160_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Delete the currently selected detail row */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 1) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedRow);
      return next.map((d, i) => ({ ...d, fd161_02: i + 1 }));
    });
    setSelectedRow(Math.max(0, selectedRow - 1));
  }, [details.length, selectedRow]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      fd160_18: 'A',  // 預設出庫類別=渤揚倉庫
      fd160_80: 'N',
    });
    setDetails([]);
    setSelectedRow(0);
  }, [clearFieldErrors]);

  const importReceiptStock = useCallback(async (receiptNo: string, warehouse: string) => {
    setLoading(true);
    try {
      const preview = await d206Api.previewFromReceiptStock(receiptNo, warehouse);
      setFormData(prev => ({ ...prev, ...preview, fd160_01: prev.fd160_01 || '' }));
      setDetails(preview.details || []);
      setSelectedRow(0);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '無法從繳庫單帶入庫存明細');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Apply result after save */
  const applyResult = useCallback((result: FD160) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    deleteDetailRow,
    revertToRecord, resetForAdd, applyResult, importReceiptStock,
    getFieldError, clearFieldErrors, hasErrors,
  };
}
